// lib/asset-rendition.ts

export const LIST_GRID_RENDITION_WIDTH = 640;
export const MAX_ASSET_RENDITION_WIDTH = 1024;
export const MIN_ASSET_RENDITION_WIDTH = 64;
export const MAX_ASSET_RENDITION_BYTES = 32 * 1024 * 1024;
export const MAX_ASSET_RENDITION_PIXELS = 36_000_000;

const INTERNAL_ASSET_PREFIX = '/api/assets/';
const SUPPORTED_FORMATS = new Set(['jpeg', 'png', 'webp', 'avif']);

/** The source stays immutable and full-sized; this is only a display rendition. */
export function buildAssetRenditionUrl(url: string, width = LIST_GRID_RENDITION_WIDTH): string | null {
  if (!Number.isInteger(width) || width < MIN_ASSET_RENDITION_WIDTH || width > MAX_ASSET_RENDITION_WIDTH) return null;
  const withoutFragment = url.split('#', 1)[0] ?? '';
  const queryIndex = withoutFragment.indexOf('?');
  const path = queryIndex === -1 ? withoutFragment : withoutFragment.slice(0, queryIndex);
  if (!isCanonicalInternalAssetPath(path)) return null;

  const query = queryIndex === -1 ? '' : withoutFragment.slice(queryIndex + 1);
  const params = new URLSearchParams(query);
  // `meta=1` is a JSON endpoint, never a drawable rendition; the original query
  // may contain a stale width too, but the display width is controlled here.
  params.delete('meta');
  params.set('width', String(width));
  return `${path}?${params.toString()}`;
}

export function parseAssetRenditionWidth(value: string | null): number | null {
  if (!value || !/^[1-9]\d{0,3}$/.test(value)) return null;
  const width = Number(value);
  return width >= MIN_ASSET_RENDITION_WIDTH && width <= MAX_ASSET_RENDITION_WIDTH ? width : null;
}

export const isSupportedAssetRenditionFormat = (format: string | undefined): boolean =>
  Boolean(format && SUPPORTED_FORMATS.has(format));

export function buildAssetRenditionEtag(sourceEtag: string | undefined, width: number): string {
  return `"${encodeURIComponent(`${sourceEtag ?? ''}:webp:76:${width}`)}"`;
}

export function assetRenditionHeaders(etag: string): Record<string, string> {
  return {
    'Content-Type': 'image/webp',
    'Cache-Control': 'private, no-cache, must-revalidate',
    'Content-Disposition': 'inline',
    'ETag': etag,
    'Vary': 'Cookie',
  };
}

function isCanonicalInternalAssetPath(path: string): boolean {
  if (!path.startsWith(INTERNAL_ASSET_PREFIX)) return false;
  const encodedAssetPath = path.slice(INTERNAL_ASSET_PREFIX.length);
  if (!encodedAssetPath || encodedAssetPath.includes('//') || /%2f|%5c/i.test(encodedAssetPath)) return false;
  try {
    const decodedAssetPath = decodeURIComponent(encodedAssetPath);
    return decodedAssetPath
      .split('/')
      .every((segment) => segment.length > 0 && segment !== '.' && segment !== '..' && !segment.includes('\\'));
  } catch {
    return false;
  }
}
